/* Priority Monitor — the catalog explorer shell (Atlas theme).
 * Sidebar, header, catalog list and form detail. Every request goes through
 * PCMAdapter (data-adapter.js); styling lives in app/static/css/atlas.css.
 */

import { Icon, RefreshIcon, PaperclipIcon } from './icons.jsx';
import { Modal, tabKeys, useTabFocus } from './accessibility.jsx';
import { showsTeam } from './team-logic.mjs';
import { TriggerHistory } from './trigger-history.jsx';
import { choices, choiceLabel, pageEnv, restorePick, PICK_PENDING, pickPending } from './company-pick.mjs';
import { rememberEnv, rememberedEnv } from './workbench-logic.mjs';

const { useState, useEffect, useRef } = React;

// ---------- Icons ----------
const I = {
  shield: <Icon d={<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>}/>,
  bolt:   <Icon d={<polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>}/>,
  users:  <Icon d={<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>}/>,
  link:   <Icon d={<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>}/>,
  alert:  <Icon d={<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>}/>,
  key:    <Icon d={<path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>}/>,
  cpu:    <Icon d={<><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/></>}/>,
  pkg:    <Icon d={<><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>}/>,
  user:   <Icon d={<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>}/>,
  search: <Icon d={<><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>}/>,
  refresh:<RefreshIcon/>,
  trend:  <Icon d={<><polyline points="22 7 13.5 15.5 8.5 10.5 1 18"/><polyline points="16 7 22 7 22 13"/></>}/>,
  activity:<Icon d={<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>}/>,
  filter: <Icon d={<polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>}/>,
  more:   <Icon d={<><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></>}/>,
  edit:   <Icon d={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>}/>,
  ledger: <Icon d={<><path d="M3 3h18v18H3z"/><line x1="3" y1="9"  x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/></>}/>,
  mail:   <Icon d={<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>}/>,
  paperclip:<PaperclipIcon/>,
  chat:   <Icon d={<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>}/>,
};

// ---------- helpers ----------
const cls = (...xs) => xs.filter(Boolean).join(' ');
// Spec §5.3: ten hues, assigned by module id modulo the palette; the pill
// tint is the hue at 12% alpha. The only hard-coded list on the page, and it
// is colors, not data -- modules still come from /api/v1/catalog/modules.
const MODULE_PALETTE = ['#0284c7', '#7c3aed', '#059669', '#b45309', '#dc2626',
                        '#db2777', '#0891b2', '#65a30d', '#4f46e5', '#0d9488'];
const moduleColor = (moduleId) => MODULE_PALETTE[Math.abs(Number(moduleId) || 0) % MODULE_PALETTE.length];
const moduleTint = (moduleId) => `${moduleColor(moduleId)}1f`;
// First letters of the first two words, upper-cased; a one-word label gives
// its first two letters. Used by the sidebar tenant chip.
const initials = (label) => {
  const words = String(label || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

import { parseIncludes } from './include-parse.mjs';

// 63,091 include directives across the corpus collapse to 10,970 distinct
// targets, and `func` alone is referenced 1,717 times. Cache the promise,
// not the value, so two chips clicked before the first resolves share one
// request. Module-level: the cache lives for the page, not the component.
const _trigCache = new Map();

function resolveTrigger(form, column, name) {
  const key = `${form}|${column}|${name}`;
  if (!_trigCache.has(key)) {
    _trigCache.set(
      key,
      PCMAdapter.getTrigger(form, column, name).then(
        (trig) => ({ kind: 'ok', trig }),
        (err) => {
          // A 404 is permanent and, per spec §8, splits into two distinct
          // dead-chip labels: the target FORM absent from form_structures
          // ("not onboarded", matches catalog.py's "No such form: ..."),
          // vs. the target TRIGGER absent from an onboarded form ("not
          // found"). Both are cached -- neither can change without an
          // onboarding. Anything else (500, a dropped connection, or the
          // three 400s from the identifier gates) is transient: evict so a
          // further click is a real retry, exactly as before.
          if (err && err.status === 404) {
            const detail = err.detail;
            const formAbsent = typeof detail === 'string' && detail.startsWith('No such form');
            return { kind: formAbsent ? 'no-form' : 'no-trigger' };
          }
          _trigCache.delete(key);
          return { kind: 'error' };
        },
      ),
    );
  }
  return _trigCache.get(key);
}

// A form's display label. Titles are frequently empty -- the dump does not
// guarantee either language -- so the ENAME is always the last resort.
function formLabel(form, lang) {
  const title = lang === 'he' ? form.title_he : form.title_en;
  return (title && title.trim()) || form.ename;
}

// Columns have a Hebrew title only. English falls back to the column name.
function columnLabel(col, lang) {
  if (lang === 'he') return (col.title && col.title.trim()) || col.name;
  return col.name;
}

// ---------- Sidebar ----------
// One helper for every nav entry: a <button>, so keyboard affordances come
// for free and the old per-item onKeyDown handlers are not needed.
function NavItem({ active, onClick, children, title }) {
  return (
    <button type="button" className={cls('nav-item', active && 'active')} onClick={onClick} title={title} aria-current={active ? 'page' : undefined}>
      {children}
    </button>
  );
}

function Sidebar({ active, onNav: navigate, lang, tenantLabel, role, me, modules, formCount, onSwitchTenant, openForm, onPickForm: pickForm, onAsk, treeUnavailable, onTreeUnavailable, menuEnv, menuNote, onResizeStart, onResizeKey, onResizeStep }) {
  const [menuOpen, setMenuOpen] = useState(() => window.innerWidth > 760);
  useEffect(() => {
    const narrow = window.matchMedia('(max-width: 760px)');
    const update = () => setMenuOpen(!narrow.matches);
    narrow.addEventListener('change', update);
    return () => narrow.removeEventListener('change', update);
  }, []);
  const closeOnMobile = () => { if (window.innerWidth <= 760) setMenuOpen(false); };
  const onNav = value => { closeOnMobile(); navigate(value); };
  const onPickForm = value => { closeOnMobile(); pickForm(value); };
  const he = lang === 'he';
  const isAdmin = role === 'admin';
  const label = tenantLabel || (role === 'admin' ? 'Admin' : 'Tenant');
  return (
    <aside className="sidebar" aria-label={he ? 'ניווט' : 'Navigation'}>
      <details className="sidebar-menu" open={menuOpen} onToggle={e => setMenuOpen(e.currentTarget.open)}>
        <summary className="sidebar-toggle">{he ? 'ניווט' : 'Navigation'}</summary>
        <div className="sidebar-body">
      <div className="brand">
        <div className="brand-logo">P</div>
        <div className="brand-name">Priority Monitor</div>
      </div>
      {/* The agent's two surfaces lead the rail: they are what a visitor came to see.
          Chat is not role-gated (see the note that used to sit on its own section). */}
      <div className="nav-section">{he ? 'הסוכן' : 'Agent'}</div>
      <nav className="nav" aria-label={he ? 'הסוכן' : 'Agent'}>
        {role === 'admin' && <NavItem active={active === '__jarvis'} onClick={() => onNav('__jarvis')}>
          <span className="ico">{I.activity}</span>
          <span className="label">Jarvis</span>
        </NavItem>}
        <NavItem active={active === '__chat'} onClick={() => onNav('__chat')}>
          <span className="ico">{I.chat}</span>
          <span className="label">{he ? 'שיחה עם הנתונים' : 'Talk to your data'}</span>
        </NavItem>
        {PCMAdapter.canUseWorkbench(role, me) && (
          <NavItem active={active === '__workbench'} onClick={() => onNav('__workbench')}>
            <span className="ico">{I.cpu}</span>
            <span className="label">{he ? 'סביבת עבודה לסוכן' : 'Agent workbench'}</span>
          </NavItem>
        )}
        {role === 'admin' && (
          <NavItem active={active === '__studio'} onClick={() => onNav('__studio')}>
            <span className="ico">{I.users}</span>
            <span className="label">{he ? 'סטודיו' : 'Studio'}</span>
          </NavItem>
        )}
      </nav>
      <div className="nav-section">{he ? 'קטלוג' : 'Catalog'}</div>
      <nav className="nav" aria-label={he ? 'קטלוג' : 'Catalog'}>
        <NavItem active={active === null} onClick={() => onNav(null)}>
          <span className="swatch"/>
          <span className="label">{he ? 'כל המסכים' : 'All forms'}</span>
          <span className="badge" title={formCount == null ? (he ? 'הספירה אינה זמינה' : 'Count unavailable') : undefined}>{formCount == null ? '—' : formCount.toLocaleString()}</span>
        </NavItem>
        {menuNote
          ? <div className="mt-note">{window.MenuTreeText?.[lang][menuNote]}</div>
          : window.MenuTree && !treeUnavailable
          ? <window.MenuTree lang={lang} openForm={openForm} onPick={onPickForm} env={menuEnv}
                             onAsk={(...args) => { closeOnMobile(); onAsk(...args); }} onUnavailable={onTreeUnavailable}/>
          : <>
            {treeUnavailable && window.MenuTreeText && (
              <div className="mt-note">{window.MenuTreeText[lang].unavailable}</div>
            )}
            {modules.map(m => (
            <NavItem key={m.module} active={active === m.module} onClick={() => onNav(m.module)}
                     title={`${m.form_count} of ${m.entry_count}`}>
              <span className="swatch" style={{ background: moduleColor(m.module) }}/>
              <span className="label" dir="auto">{m.module_name}</span>
              <span className="badge">{m.form_count == null ? '—' : m.form_count}</span>
            </NavItem>
            ))}
          </>}
      </nav>
      {/* Chat above is not role-gated: dashboard-v2.html is only ever reached
          with a tenant token in localStorage -- direct tenant login sets it, and
          an admin session sets it via impersonation before it ever lands
          here (index.html's tenant picker) -- so /api/v1/chat's tenant-JWT
          requirement is met either way. */}
      {/* Tenant-visible, not admin-gated: watching how fresh the mirror is
          changes nothing (app/routers/refresh.py). No status dot here: the
          mock drew a green one, but nothing drives it, and green without
          evidence is the one thing the old design-system doc forbade. */}
      <div className="nav-section">{he ? 'מראה' : 'Mirror'}</div>
      <nav className="nav" aria-label={he ? 'מראה' : 'Mirror'}>
        <NavItem active={active === '__freshness'} onClick={() => onNav('__freshness')}>
          <span className="ico">{I.refresh}</span>
          <span className="label">{he ? 'טריות המראה' : 'Mirror freshness'}</span>
        </NavItem>
      </nav>
      {/* A tenant login's own agent seat and, for a company admin, its team
          (agent seats spec §6). Administration follows its own switch: team-logic.mjs. */}
      {role !== 'admin' && showsTeam(me) && (
        <>
          <div className="nav-section">{he ? 'צוות' : 'Team'}</div>
          <nav className="nav" aria-label={he ? 'צוות' : 'Team'}>
            <NavItem active={active === '__team'} onClick={() => onNav('__team')}>
              <span className="ico">{I.users}</span>
              <span className="label">{he ? 'הסוכן והצוות שלי' : 'My agent and team'}</span>
            </NavItem>
          </nav>
        </>
      )}
      {me?.team_admin_offline && !me.via_admin && <nav className="nav" aria-label="Team Admin (offline)">
        <NavItem active={active === '__team_offline'} onClick={() => onNav('__team_offline')}>
          <span className="ico">{I.users}</span><span className="label">Team Admin (offline)</span>
        </NavItem>
      </nav>}
      {/* A company admin's own Settings (spec 2026-10-18-full-separation §7.2), locked to
          its company; an installation admin has Settings under System instead. */}
      {PCMAdapter.canEditCompany(role, me) && (
        <nav className="nav" aria-label={he ? 'הגדרות' : 'Settings'}>
          <NavItem active={active === '__settings'} onClick={() => onNav('__settings', '#company')}>
            <span className="ico">{I.key}</span>
            <span className="label">{he ? 'הגדרות' : 'Settings'}</span>
          </NavItem>
        </nav>
      )}
      {role === 'admin' && (
        <>
          <div className="nav-section">{he ? 'מערכת' : 'System'}</div>
          <nav className="nav" aria-label={he ? 'מערכת' : 'System'}>
            <NavItem active={active === '__backups'} onClick={() => onNav('__backups')}>
              <span className="ico">{I.pkg}</span>
              <span className="label">{he ? 'גיבויים' : 'Backups'}</span>
            </NavItem>
            <NavItem active={active === '__settings'} onClick={() => onNav('__settings')}>
              <span className="ico">{I.key}</span>
              <span className="label">{he ? 'הגדרות' : 'Settings'}</span>
            </NavItem>
            <NavItem active={active === '__sync'} onClick={() => onNav('__sync')}>
              <span className="ico">{I.link}</span>
              <span className="label">{he ? 'בקרת סנכרון' : 'Sync control'}</span>
            </NavItem>
            <NavItem active={active === '__jobs'} onClick={() => onNav('__jobs')}>
              <span className="ico">{I.mail}</span>
              <span className="label">{he ? 'עבודות יוצאות' : 'Outgoing jobs'}</span>
            </NavItem>
            <NavItem active={active === '__outlook'} onClick={() => onNav('__outlook')}>
              <span className="ico">{I.paperclip}</span>
              <span className="label">Outlook</span>
            </NavItem>
          </nav>
        </>
      )}
      {(role==='admin'||me?.tenant_admin)&&<nav className="nav" aria-label={he ? 'שימוש במודלי שפה' : 'LLM Usage'}><NavItem active={active==='__llm_usage'} onClick={()=>onNav('__llm_usage')}><span className="ico">{I.activity}</span><span className="label">{he?'שימוש במודלי שפה':'LLM Usage'}</span></NavItem></nav>}
      <div className="mt-size-controls">
        <button type="button" className="btn btn-sm" aria-label={he ? 'הקטן רוחב הסרגל' : 'Narrow sidebar'} onClick={() => onResizeStep(false)}>−</button>
        <button type="button" className="btn btn-sm" aria-label={he ? 'הגדל רוחב הסרגל' : 'Widen sidebar'} onClick={() => onResizeStep(true)}>+</button>
      </div>
      <div className="sidebar-foot">
        {/* Admins can switch tenant from here; a tenant session cannot, so
            the chip is inert for them and rendered as non-interactive metadata. */}
        {isAdmin ? (
          <button type="button"
                  className="tenant-chip clickable"
                  onClick={onSwitchTenant}
                  title={he ? 'החלף לקוח' : 'Switch tenant'}>
            <span className="avatar">{initials(label)}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="name" dir="auto">{label}</span>
              <span className="role-line">{he ? 'מנהל · מתחזה' : 'admin · impersonating'}</span>
            </span>
          </button>
        ) : (
          <div className="tenant-chip">
            <span className="avatar">{initials(label)}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="name" dir="auto">{label}</span>
              <span className="role-line">{he ? 'לקוח' : 'tenant'}</span>
            </span>
          </div>
        )}
      </div>
        </div>
      </details>
      <div className="mt-resizer" onPointerDown={onResizeStart}
           role="separator" aria-orientation="vertical" tabIndex={0} aria-valuenow={248}
           aria-label={he ? 'שינוי רוחב הסרגל' : 'Resize sidebar'}
           aria-valuemin={220} aria-valuemax={520}
           onKeyDown={onResizeKey}/>
    </aside>
  );
}

// ---------- Topbar ----------
function AccountMenu({ he, me, role, onChangePassword, onMcpTokens, onMcpProposals, onSwitchTenant, onSwitchContext, onLogout }) {
  const ref = useRef(null);
  useEffect(() => {
    const outside = event => { if (ref.current && !ref.current.contains(event.target)) ref.current.open = false; };
    const escape = event => {
      if (event.key !== 'Escape' || !ref.current?.open) return;
      event.preventDefault();
      ref.current.open = false;
      ref.current.querySelector('summary').focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, []);
  const username = me?.username || (he ? 'חשבון' : 'Account');
  return <details ref={ref} className="account-menu" onBlur={event => {
    if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false;
  }}>
    <summary className="account-trigger" aria-label={`${he ? 'תפריט חשבון' : 'Account menu'}: ${username}`}>
      <span className="account-avatar" aria-hidden="true">{I.user}</span>
      <span className="account-name" dir="auto">{username}</span>
      <Icon d={<polyline points="6 9 12 15 18 9"/>}/>
    </summary>
    <div className="account-menu-panel" onClickCapture={event => {
      if (!event.target.closest('button')) return;
      ref.current.open = false;
      ref.current.querySelector('summary').focus();
    }}>
      <div className="account-menu-heading" dir="auto">{me?.display_name || username}</div>
      {me && me.user_id != null && <>
        {onChangePassword && <button type="button" className="btn btn-sm" onClick={onChangePassword}>{he ? 'שינוי סיסמה' : 'Change password'}</button>}
        {onMcpTokens && (me.role === 'admin' || !me.via_admin) && <button type="button" className="btn btn-sm" onClick={onMcpTokens}>{he ? 'טוקנים ל-MCP' : 'MCP tokens'}</button>}
        {onMcpProposals && PCMAdapter.canReviewMcpProposals(role, me) && <button type="button" className="btn btn-sm" onClick={onMcpProposals}>{he ? 'אישורי MCP' : 'MCP approvals'}</button>}
      </>}
      {onSwitchTenant && role === 'admin' && <button type="button" className="btn btn-sm" onClick={onSwitchTenant}>{he ? 'החלף לקוח' : 'Switch tenant'}</button>}
      {me?.role === 'tenant' && me.user_id != null && !me.via_admin && <button type="button" className="btn btn-sm" onClick={onSwitchContext}>{he ? 'החלפת סביבת עבודה' : 'Switch workspace'}</button>}
      <button type="button" className="btn btn-sm danger" onClick={onLogout}>{he ? 'יציאה' : 'Log out'}</button>
    </div>
  </details>;
}

function Topbar({ now, lang, setLang, viewLabel, onLogout, onSwitchTenant, onSwitchContext, role, lastIngestAt, lastIngestSource, nextIngestAt, nextIngestSource, me, onChangePassword, onMcpTokens, onMcpProposals, chatActivity, onReturnToChat, companyPick }) {
  // Age = time since the most recent ingest_log row arrived for this
  // tenant — *not* time since the last UI poll. Empty until the first
  // source-freshness response comes back.
  const ageSec = lastIngestAt ? Math.max(0, Math.floor((now - lastIngestAt) / 1000)) : null;
  const ageLabel = ageSec == null ? '—'
    : ageSec < 5 ? 'just now'
    : ageSec < 60 ? `${ageSec}s ago`
    : ageSec < 3600 ? `${Math.floor(ageSec / 60)}m ago`
    : `${Math.floor(ageSec / 3600)}h ago`;
  // Color-tier the freshness pill by data age. Thresholds widened — we're
  // now showing real ingest cadence, not the 30s polling tick.
  const ageTier = ageSec == null ? 'dim'
    : ageSec < 5 * 60   ? 'fresh'   // < 5 min — recently received
    : ageSec < 30 * 60  ? 'aging'   // < 30 min
    : 'stale';                      // >= 30 min — Priority probably stuck
  const sourceLabel = lastIngestSource || '';
  const ageTitle = lastIngestAt
    ? `Last ingest: ${sourceLabel || 'unknown source'} at ${new Date(lastIngestAt).toLocaleString()}`
    : 'Waiting for first ingest from Priority';

  // Predicted next ingest from the soonest source's observed cadence
  // (median of consecutive deltas over the last 7 days). Negative diffs
  // mean we're past the projected time — Priority is overdue, not a bug.
  const etaSec = nextIngestAt ? Math.floor((nextIngestAt - now) / 1000) : null;
  const etaLabel = etaSec == null ? '—'
    : etaSec <= 0 ? 'due now'
    : etaSec < 60 ? `in ${etaSec}s`
    : etaSec < 3600 ? `in ${Math.floor(etaSec / 60)}m`
    : `in ${Math.floor(etaSec / 3600)}h`;
  const etaTier = etaSec == null ? 'dim'
    : etaSec <= -300 ? 'stale'   // overdue by 5+ minutes
    : etaSec <= 0 ? 'aging'      // due to overdue
    : 'fresh';                   // upcoming
  const etaTitle = nextIngestAt
    ? `Next expected ingest: ${nextIngestSource || 'soonest source'} at ${new Date(nextIngestAt).toLocaleString()} — projected from observed cadence over the last 7 days`
    : 'No cadence data yet — need at least 2 ingests per source to project';
  const he = lang === 'he';
  const ageClass = ageTier === 'fresh' ? 'st-ok' : ageTier === 'aging' ? 'st-warn' : 'st-danger';
  const etaClass = etaTier === 'fresh' ? 'st-info' : etaTier === 'aging' ? 'st-warn' : 'st-danger';
  return (
    <header className="topbar">
      <div className="crumbs">
        <span>{he ? 'מרכז שליטה' : 'Command center'}</span>
        <span className="sep">›</span>
        <span className="now" dir="auto">{viewLabel}</span>
      </div>
      <div className="grow"/>
      {chatActivity && (
        <div className="chat-activity">
          <span className="pill st-info" role="status">{he ? 'התשובה מתבצעת' : 'Reply running'}</span>
          <button type="button" className="btn btn-sm" onClick={onReturnToChat}>{he ? 'חזרה לשיחה' : 'Return to chat'}</button>
          <button type="button" className="btn btn-sm" onClick={chatActivity.stop}>{he ? 'עצור' : 'Stop'}</button>
        </div>
      )}
      {/* Spec §2.1: these two render only when their props are non-null.
          Today App passes null, so nothing shows; the code stays so wiring
          a source later is a one-line change in App. */}
      {lastIngestAt && (
        <span className={cls('pill', ageClass)} title={ageTitle}>
          <span className="dot live"/>
          {he ? 'עודכן' : 'Ingested'} {ageLabel}{sourceLabel && <span className="mono"> · {sourceLabel}</span>}
        </span>
      )}
      {nextIngestAt && (
        <span className={cls('pill', etaClass)} title={etaTitle}>
          {he ? 'הבא' : 'Next'} {etaLabel}{nextIngestSource && <span className="mono"> · {nextIngestSource}</span>}
        </span>
      )}
      {companyPick && companyPick.pick && <CompanyPicker he={he} companyPick={companyPick}/>}
      <div className="seg seg-sm" role="group" aria-label="Language">
        <button type="button" className={cls(!he && 'on')} onClick={() => setLang('en')}>EN</button>
        <button type="button" className={cls(he && 'on')} onClick={() => setLang('he')}>עב</button>
      </div>
      <AccountMenu he={he} me={me} role={role} onChangePassword={onChangePassword}
                   onMcpTokens={onMcpTokens} onMcpProposals={onMcpProposals}
                   onSwitchTenant={onSwitchTenant} onSwitchContext={onSwitchContext} onLogout={onLogout}/>
    </header>
  );
}

// The offline workspace's one company picker (spec 2026-11-02 §4.3). An option's value is its index
// in choices(): a company with no upload has key null, which a string pair would not round-trip.
function CompanyPicker({ he, companyPick }) {
  const list = choices(companyPick.companies);
  const { pick } = companyPick;
  const at = list.findIndex(c => c.company_env === pick.company_env && c.key === pick.key);
  return (
    <select className="select" aria-label={he ? 'חברה' : 'Company'} value={String(at)}
            onChange={e => companyPick.choose(list[Number(e.target.value)])}>
      {companyPick.companies.map(company => (
        <optgroup key={company.company_env} label={company.title}>
          {list.map((c, i) => c.company_env === company.company_env &&
            <option key={i} value={String(i)}>{choiceLabel(c, he)}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

function ContextDialog({ lang, onClose }) {
  const he = lang === 'he';
  const [contexts, setContexts] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const errorText = e => {
    const detail = e?.detail;
    if (typeof detail === 'string' && detail) return detail;
    return String(detail?.message || e?.message || e);
  };
  useEffect(() => {
    let active = true;
    PCMAdapter.getContexts().then(d => { if (active) setContexts(d.contexts); })
      .catch(e => { if (active) setError(errorText(e)); });
    return () => { active = false; };
  }, []);
  const choose = async tenantId => {
    setBusy(true); setError('');
    try { await PCMAdapter.selectContext(tenantId); }
    catch (e) { setError(errorText(e)); setBusy(false); }
  };
  return <Modal label={he ? 'החלפת סביבת עבודה' : 'Switch workspace'} onClose={onClose}>
    <h2>{he ? 'החלפת סביבת עבודה' : 'Switch workspace'}</h2>
    <p className="set-note">{he ? 'הכניסה נשארת עם אותו משתמש והרשאותיו.' : 'Continue as the same named user with your existing permissions.'}</p>
    {!contexts && !error && <p role="status">{he ? 'טוען…' : 'Loading…'}</p>}
    {error && <p className="set-error" role="alert">{error}</p>}
    <div className="set-row">{contexts?.map(c => <button type="button" className="btn" key={c.tenant_id}
      disabled={busy} onClick={() => choose(c.tenant_id)}>{c.tenant_label}</button>)}</div>
    <div className="dlg-actions"><button type="button" className="btn btn-sm" onClick={onClose}>{he ? 'ביטול' : 'Cancel'}</button></div>
  </Modal>;
}

// ---------- Pagination control (card foot) ----------
function Pager({ page, total, limit, shown, onChange, lang }) {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = (page - 1) * limit + shown;
  return (
    <div className="pager">
      <span className="page-info">{from}–{to} / {(total == null ? '—' : total.toLocaleString())}</span>
      <div className="arrows">
        <button type="button" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label={lang === 'he' ? 'העמוד הקודם' : 'Previous page'}>‹</button>
        <button type="button" disabled={total == null || page >= totalPages} onClick={() => onChange(page + 1)} aria-label={lang === 'he' ? 'העמוד הבא' : 'Next page'}>›</button>
      </div>
    </div>
  );
}

function LoadingCard({ lang }) {
  return <div className="card"><div className="card-body dim">{lang === 'he' ? 'טוען…' : 'Loading…'}</div></div>;
}

function EmptyTabState({ lang, source, hint }) {
  const messages = {
    CATALOG_FORMS: {
      en: 'No forms match. The catalog only holds forms whose structure has been received.',
      he: 'אין מסכים תואמים. הקטלוג מכיל רק מסכים שמבנם התקבל.',
    },
    CATALOG_TRIGGERS: {
      en: 'No triggers on this form.',
      he: 'אין טריגרים במסך זה.',
    },
    FORM_NO_BASE_TABLE: {
      en: 'This form records no base table, so there is no mirror table to show.',
      he: 'למסך זה לא רשומה טבלת בסיס, ולכן אין טבלת מראה להציג.',
    },
  };
  const msg = messages[source]?.[lang === 'he' ? 'he' : 'en'] || (
    lang === 'he' ? 'אין נתונים זמינים' : 'No data available'
  );
  return (
    <div className="card empty-tab-card">
      <div className="empty-tab-icon" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="8" y1="14" x2="16" y2="14"/>
          <line x1="9" y1="9" x2="9.01" y2="9"/>
          <line x1="15" y1="9" x2="15.01" y2="9"/>
        </svg>
      </div>
      <div className="empty-tab-text">
        <h4>{lang === 'he' ? 'שקט עכשיו' : 'Quiet right now'}</h4>
        <p>{msg}</p>
        {hint && <p className="dim">{hint}</p>}
      </div>
    </div>
  );
}

// ---------- Catalog: form list ----------
function CatalogView({ lang, module, onPick, modules }) {
  const [q, setQ] = useState('');
  const [level, setLevel] = useState('');
  // form_structures holds Priority screens AND the T_<TNAME> dictionaries
  // the table mirror keeps for itself -- 3,258 of 8,056 rows on production,
  // title-less in English and not screens at all. Default to screens.
  const [kind, setKind] = useState('screens');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const LIMIT = 100;
  const he = lang === 'he';

  useEffect(() => { setPage(1); }, [q, level, kind, module]);

  useEffect(() => {
    let live = true;
    setErr('');
    PCMAdapter.getForms({
      q: q || undefined,
      kind,
      module: module ?? undefined,
      level: level || undefined,
      limit: LIMIT,
      offset: (page - 1) * LIMIT,
    })
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setErr(String(e.message || e)); });
    return () => { live = false; };
  }, [q, level, kind, module, page]);

  if (err) return <div role="alert" className="error-pane" dir="auto">{err}</div>;
  if (!data) return <LoadingCard lang={lang}/>;

  // The eyebrow names the active module. FormRow carries no module, so a
  // per-row module pill is not possible from this endpoint (spec §7.1 note).
  const mod = module == null ? null : (modules || []).find(m => m.module === module);
  const modName = module == null ? (he ? 'כל המסכים' : 'All forms') : (mod ? mod.module_name : String(module));
  const modColor = module == null ? 'var(--ink)' : moduleColor(module);
  const levels = [['', he ? 'הכל' : 'All'], ['1', 'L1'], ['2', 'L2'], ['3', 'L3']];
  // The heading counts what the scope actually lists, so it has to name it:
  // under `all` the list is both populations, and calling that "Forms" makes
  // the number look like a form count.
  const kindLabel = kind === 'tables'
    ? (he ? 'טבלאות' : 'Tables')
    : kind === 'all'
      ? (he ? 'מסכים וטבלאות' : 'Forms & tables')
      : (he ? 'מסכים' : 'Forms');
  const kinds = [
    ['screens', he ? 'מסכים' : 'Screens'],
    ['tables', he ? 'טבלאות' : 'Tables'],
    ['all', he ? 'הכל' : 'All'],
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-eyebrow" style={{ color: modColor }}>
            <span className="swatch"/><span dir="auto">{modName}</span>
          </div>
          <h1 className="page-title">
            {kindLabel}{' '}
            <span className="count">{(data.total == null ? '—' : data.total.toLocaleString())}</span>
          </h1>
        </div>
        <div className="page-actions">
          <label className="search">
            {I.search}
            <input aria-label={he ? 'חיפוש מסכים וטבלאות' : 'Search forms and tables'} value={q} placeholder={he ? 'חיפוש לפי מסך או טבלה…' : 'Search by form or table…'} onChange={e => setQ(e.target.value)}/>
          </label>
          <div className="seg" role="group" aria-label={he ? 'סוג' : 'Kind'}>
            {kinds.map(([v, label]) => (
              <button type="button" key={v} className={cls(kind === v && 'on')} onClick={() => setKind(v)}>{label}</button>
            ))}
          </div>
          <div className="seg" role="group" aria-label={he ? 'רמה' : 'Level'}>
            {levels.map(([v, label]) => (
              <button type="button" key={v || 'all'} className={cls(level === v && 'on')} onClick={() => setLevel(v)}>{label}</button>
            ))}
          </div>
        </div>
      </div>
      {window.FormViewStrip && (
        <window.FormViewStrip lang={lang} onOpenForm={onPick}/>
      )}
      {data.total === 0 && (
        <EmptyTabState lang={lang} source="CATALOG_FORMS"
          hint={he ? 'נסו חיפוש או מסנן אחר' : 'Try a different search or filter'}/>
      )}
      {data.items.length > 0 && (
        <div className="card">
          <div className="tbl-wrap" tabIndex={0}>
            <table className="tbl tbl-clickable">
              <thead>
                <tr>
                  <th>{he ? 'כותרת' : 'Title'}</th>
                  <th>ENAME</th>
                  <th>{he ? 'רמה' : 'Level'}</th>
                  <th>{he ? 'שורש' : 'Root'}</th>
                  <th>{he ? 'טבלת בסיס' : 'Base table'}</th>
                  <th>{he ? 'טריגרים' : 'Triggers'}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(f => (
                  <tr key={f.ename} onClick={() => onPick(f.ename)} className={f.granted === false ? 'perm-ungranted' : ''}>
                    <td className="he-text" dir="auto"><button type="button" className="btn link" onClick={e => { e.stopPropagation(); onPick(f.ename); }}>{formLabel(f, lang)}</button></td>
                    <td className="mono">{f.ename}{f.granted === false && <span className="perm-lock" title={he ? 'לא הוענקה' : 'not granted'}>🔒</span>}</td>
                    <td className="muted">{f.level}</td>
                    <td className="mono">{f.root}</td>
                    <td className="mono">{f.base_table}</td>
                    <td>
                      <span className="trig-cell">
                        {f.has_form_triggers && <span className="trig-dot f" title="form triggers">F</span>}
                        {f.has_column_triggers && <span className="trig-dot c" title="column triggers">C</span>}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} total={data.total} limit={LIMIT} shown={data.items.length} onChange={setPage} lang={lang}/>
        </div>
      )}
    </>
  );
}

// ---------- Catalog: form detail ----------
function FormDetailView({ ename, lang, onBack, module }) {
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState('columns');
  const tabFocus = useTabFocus(`form-tab-${tab}`);
  const [err, setErr] = useState('');
  const he = lang === 'he';

  useEffect(() => {
    let live = true;
    setForm(null); setErr('');
    PCMAdapter.getForm(ename)
      .then(d => { if (live) setForm(d); })
      .catch(e => { if (live) setErr(String(e.message || e)); });
    return () => { live = false; };
  }, [ename]);

  if (err) return <div role="alert" className="error-pane" dir="auto">{err}</div>;
  if (!form) return <LoadingCard lang={lang}/>;

  const custom = form.triggers.filter(tr => tr.is_custom);
  const standard = form.triggers.filter(tr => !tr.is_custom);
  const swatch = module == null ? 'var(--ink)' : moduleColor(module);
  const tabs = [
    ['columns', he ? 'עמודות' : 'Columns', form.columns.length],
    ['triggers', he ? 'טריגרים' : 'Triggers', form.triggers.length],
    ['data', he ? 'נתונים' : 'Data', null],
    ['view', he ? 'תצוגה' : 'View', null],
  ];
  const brokenTab = (what) => (
    <div className="card"><div className="card-body">
      {he ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : `${what} tab failed to load — a hard reload may be required.`}
    </div></div>
  );
  const goTriggers = () => { setTab('triggers'); window.scrollTo({ top: 0 }); };

  return (
    <>
      <div>
        <button type="button" className="btn link" onClick={onBack} style={{ marginBottom: 12 }}>
          ← {he ? 'חזרה למסכים' : 'Back to forms'}
        </button>
        <div className="form-head">
          <div className="swatch" style={{ background: swatch }}/>
          <div>
            <h1 className="page-title" dir="auto">{formLabel(form, lang)}</h1>
            <div className="form-meta" dir="ltr">
              {[...form.ancestors].reverse().map(a => (
                <span key={a.ename}><span dir="auto">{formLabel(a, lang)}</span> › </span>
              ))}
              <strong>{form.ename}</strong>
              {' · '}{form.base_table}{' · L'}{form.level}
              {form.title_he && form.title_en && (
                <>{' · '}<span className="alt-title" dir="auto">{he ? form.title_en : form.title_he}</span></>
              )}
            </div>
          </div>
        </div>
      </div>

      <div ref={tabFocus.ref} className="tabs" role="tablist" aria-label={he ? 'פרטי המסך' : 'Form details'} onKeyDown={tabKeys} onFocus={tabFocus.onFocus}>
        {tabs.map(([k, label, count]) => (
          <button type="button" key={k} role="tab" id={`form-tab-${k}`} aria-controls="form-panel" tabIndex={tabFocus.focused === `form-tab-${k}` ? 0 : -1} aria-selected={tab === k} className={cls('tab', tab === k && 'active')} onClick={() => setTab(k)}>
            {label}{count !== null && <span className="badge">{count}</span>}
          </button>
        ))}
      </div>

      <div id="form-panel" role="tabpanel" aria-labelledby={`form-tab-${tab}`} tabIndex={0}>
      {tab === 'columns' && (
        <div className="card">
          <div className="tbl-wrap" tabIndex={0}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{he ? 'כותרת' : 'Title'}</th>
                  <th>{he ? 'עמודה' : 'Column'}</th>
                  <th>{he ? 'טיפוס' : 'Type'}</th>
                  <th>{he ? 'קישור' : 'Join'}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.columns.map(c => (
                  <tr key={c.name} className={cls(c.hidden && 'row-dim')}>
                    <td className="he-text" dir="auto" style={{ fontWeight: 500 }}>{columnLabel(c, lang)}</td>
                    <td className="mono">{c.name}</td>
                    <td className="mono">
                      {c.ptype}({c.width}){c.precision ? `.${c.precision}` : ''}
                    </td>
                    <td className="mono" style={{ color: 'var(--accent)' }}>
                      {c.join_table ? `${c.join_table}.${c.join_column}` : ''}
                    </td>
                    <td>
                      <span className="flags">
                        {c.is_key && <span className="flag key">KEY</span>}
                        {c.hidden && <span className="flag">hidden</span>}
                        {c.computed && <span className="flag" title={c.expr}>calc</span>}
                        {c.read_only && <span className="flag">ro</span>}
                        {c.has_triggers && (
                          <span className="flag trig" role="button" tabIndex={0}
                                onClick={goTriggers}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTriggers(); } }}>trig</span>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'triggers' && (
        <div className="triggers">
          {form.triggers.length === 0 && (
            <EmptyTabState lang={lang} source="CATALOG_TRIGGERS"/>
          )}
          {custom.length > 0 && (
            <h3 className="trig-group">{he ? 'התאמות אישיות' : 'Customizations'} ({custom.length})</h3>
          )}
          {custom.map(tr => <TriggerBlock key={`c${tr.column}${tr.name}`} tr={tr} lang={lang} form={form.ename}/>)}
          {standard.length > 0 && (
            <h3 className="trig-group">{he ? 'סטנדרטי' : 'Standard'} ({standard.length})</h3>
          )}
          {standard.map(tr => <TriggerBlock key={`s${tr.column}${tr.name}`} tr={tr} lang={lang} form={form.ename}/>)}
        </div>
      )}
      {/* The mirror ships tables, not forms: since 2026-08-27 every receipt
          arrives under `T_<TNAME>`, so asking for this form's own name
          returned nothing for all 8,056 forms. `T_<base_table>` is the row
          store behind this screen; the assembled form is the View tab. */}
      {tab === 'data' && (
        // base_table is `nullable=False, default=""` on the model, so an
        // empty one is reachable even though no production row has one
        // today. Ungated it builds the name `T_`, which 404s, and the tab
        // shows a raw error where "there is nothing to show" is the truth.
        !form.base_table
          ? <EmptyTabState lang={lang} source="FORM_NO_BASE_TABLE"/>
          : window.FormDataTab
            ? <window.FormDataTab ename={`T_${form.base_table}`} formEname={form.ename} lang={lang}/>
            : brokenTab('Data'))}
      {tab === 'view' && (window.FormViewTab ? <window.FormViewTab ename={ename} lang={lang}/> : brokenTab('View'))}
      </div>
    </>
  );
}

function IncludeChip({ inc, lang }) {
  // idle | loading | open | no-form | no-trigger | error.
  // no-form/no-trigger are terminal (spec §8: "not onboarded" / "not
  // found"); error is transient and stays clickable to retry.
  const [state, setState] = useState('idle');
  const [trig, setTrig] = useState(null);

  const label = inc.column
    ? `${inc.form}/${inc.column}/${inc.name}`
    : `${inc.form}/${inc.name}`;

  const onClick = () => {
    if (state === 'open') { setState('idle'); return; }
    if (trig) { setState('open'); return; }
    if (state === 'no-form' || state === 'no-trigger') return; // terminal, no re-request
    setState('loading');
    resolveTrigger(inc.form, inc.column, inc.name).then((res) => {
      if (res.kind === 'ok') { setTrig(res.trig); setState('open'); }
      else { setState(res.kind); }
    });
  };

  const isDead = state === 'no-form' || state === 'no-trigger';
  const isError = state === 'error';
  const suffix =
    state === 'loading' ? ' …'
    : state === 'no-form' ? (lang === 'he' ? ' · לא נטען' : ' · not onboarded')
    : state === 'no-trigger' ? (lang === 'he' ? ' · לא נמצא' : ' · not found')
    : state === 'error' ? (lang === 'he' ? ' · נסה שוב' : ' · retry')
    : '';

  return (
    <span className="inc-wrap">
      <span
        className={cls(
          'inc-chip',
          state === 'open' && 'open',
          isDead && 'dead',
          isError && 'retry',
        )}
        role="button"
        tabIndex={0}
        aria-expanded={state === 'open'}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
        title={label}
      >
        {inc.raw}{suffix}
      </span>
      {state === 'open' && trig && (
        <span className="inc-body">
          <TriggerBlock
            tr={trig}
            lang={lang}
            heading={label}
            form={inc.form}
            nested
          />
        </span>
      )}
    </span>
  );
}

function TriggerBlock({ tr, lang, heading, nested = false, form }) {
  const [open, setOpen] = useState(nested || tr.is_custom);
  const [historyOpen, setHistoryOpen] = useState(false);
  const historyId = React.useId();
  const segments = open ? parseIncludes(tr.sql) : [];
  const he = lang === 'he';
  return (
    <div className={cls('trig-block', tr.is_custom && 'custom', nested && 'nested')}>
      <div className="trig-header">
      <div className="trig-head" onClick={() => setOpen(!open)} role="button" aria-expanded={open}
           tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(!open); } }}>
        <span className="mono trig-name">
          {heading || `${tr.column ? `${tr.column}.` : ''}${tr.name}`}
        </span>
        <span className={cls('chip', tr.is_custom ? 'st-warn' : 'st-info')}>
          {tr.is_custom ? (he ? 'התאמה' : 'custom') : (he ? 'סטנדרטי' : 'standard')}
        </span>
        <span className="grow"/>
        <span className="trig-scope">{tr.scope}</span>
      </div>
      <button type="button" className="btn btn-sm trig-history-toggle" aria-expanded={historyOpen}
        aria-controls={historyOpen ? historyId : undefined} onClick={() => { setHistoryOpen(!historyOpen); setOpen(true); }}>
        {he ? 'היסטוריה' : 'History'}
      </button>
      </div>
      {/* Priority SQL from a customer's ERP: rendered as text, never markup.
          #INCLUDE runs become chips; everything else stays verbatim. */}
      {open && (
        <pre className="trig-sql" dir="ltr">
          {segments.map((s, i) => (
            s.type === 'text'
              ? <React.Fragment key={i}>{s.text}</React.Fragment>
              : <IncludeChip key={i} inc={s} lang={lang}/>
          ))}
        </pre>
      )}
      {historyOpen && <TriggerHistory key={`${form}/${tr.column || ''}/${tr.name}`} ename={form} tr={tr} lang={lang} id={historyId}/>}
    </div>
  );
}

// ---------- Root ----------
const LANG_KEY = 'pcm_lang';
const readLang = () => {
  try { return localStorage.getItem(LANG_KEY) === 'he' ? 'he' : 'en'; } catch (_) { return 'en'; }
};

function App() {
  const [lang, setLangState] = useState(readLang);
  const [backupCloseUnconfirmed] = useState(() => {
    try {
      const unconfirmed = sessionStorage.getItem('pcm_backup_close_unconfirmed') === '1';
      sessionStorage.removeItem('pcm_backup_close_unconfirmed');
      return unconfirmed;
    } catch (_) { return false; }
  });
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem(LANG_KEY, l); } catch (_) { /* private mode: in-memory only */ }
  };
  const [modules, setModules] = useState([]);
  const [formCount, setFormCount] = useState(null);
  const [me, setMe] = useState(null);
  const fromHash = () => PCMAdapter.dashboardRoute(window.location.hash, PCMAdapter.getRole(), me);
  const [target, setTarget] = useState(fromHash);
  const [activeModule, setActiveModule] = useState(() => fromHash().module);
  const [openForm, setOpenForm] = useState(null);
  const [now, setNow] = useState(new Date());
  const [treeUnavailable, setTreeUnavailable] = useState(false);
  // The offline workspace's pick (spec 2026-11-02 §4.3); PICK_PENDING while it loads, then null on a
  // connected tenant and on any error.
  const [companyPick, setCompanyPick] = useState(() => (PCMAdapter.getToken() ? PICK_PENDING : null));
  const [chatDraft, setChatDraft] = useState('');
  const [chatVisited, setChatVisited] = useState(() => fromHash().module === '__chat');
  const [chatActivity, setChatActivity] = useState(null);
  useEffect(() => { if (activeModule === '__chat') setChatVisited(true); }, [activeModule]);
  // M3-D13 (#chat/task/<id>, ask_render.task_link): a deep-linked task id,
  // handed to ChatPage exactly the way chatDraft is -- consumed once, so
  // revisiting the chat page later does not re-select it. The workbench's
  // own link needs no equivalent: `target` already reaches WorkbenchPage.
  const [linkedTaskId, setLinkedTaskId] = useState(() => fromHash().taskId ?? null);
  const [pwOpen, setPwOpen] = useState(false);
  const [mcpOpen, setMcpOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const mainRef = useRef(null);
  const routeMounted = useRef(false);
  useEffect(() => {
    if (routeMounted.current) mainRef.current?.focus();
    routeMounted.current = true;
  }, [activeModule, openForm]);

  const role = PCMAdapter.getRole();
  const tenantLabel = PCMAdapter.getTenantLabel();
  useEffect(() => {
    const onHash = () => {
      const next = fromHash();
      setTarget(next); setActiveModule(next.module); setOpenForm(null);
      if (next.taskId != null) setLinkedTaskId(next.taskId);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [me]);


  // The topbar's age/eta labels are relative to `now`; a minute tick is
  // enough for "3m ago"-grade text.
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);

  useEffect(() => {
    PCMAdapter.getMe().then(profile => {
      setMe(profile);
      const next = PCMAdapter.dashboardRoute(window.location.hash, PCMAdapter.getRole(), profile);
      setTarget(next); setActiveModule(next.module);
    }).catch(() => setMe(null));
  }, []);
  // Beside getMe, not after it (ruling P18): a connected tenant waits on its 404 for no longer than the view
  // already waits. Any other failure is asked once more, then the page is today's.
  useEffect(() => {
    if (!PCMAdapter.getToken()) return;
    let pickWrite = Promise.resolve();   // one PUT at a time, so the last pick is the stored one
    PCMAdapter.getOfflinePicker()
      .catch(e => (e.status === 404 ? Promise.reject(e) : PCMAdapter.getOfflinePicker()))
      .then(res => {
        const tenantId = res.tenant_id;
        const choose = c => {
          setCompanyPick(p => p && { ...p, pick: c });
          rememberEnv(tenantId, pageEnv(c));
          // The browser copy above still holds when the server's does not.
          if (res.remembers) {
            pickWrite = pickWrite
              .then(() => PCMAdapter.putOfflinePicker({ company_env: c.company_env, key: c.key }))
              .catch(() => {});
          }
        };
        setCompanyPick({ tenantId, companies: res.companies, choose,
          pick: restorePick({ server: res.pick, remembered: rememberedEnv(tenantId), companies: res.companies }) });
      }).catch(() => setCompanyPick(null));   // 404: a connected installation, the page as today
  }, []);

  // Spec §5.4: direction lives on <html>. <body> carries no dir attribute
  // (dashboard-v2.html, Task 3), so this is the only place it is set.
  useEffect(() => {
    document.documentElement.setAttribute('dir', lang === 'he' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  // Sidebar width: read once from localStorage, clamped, and set on <html>
  // as a CSS variable so `.shell` and `.mt-resizer` (atlas.css) both track
  // it. The summit default is 248px; existing saved widths remain authoritative.
  useEffect(() => {
    let w = 248;
    try { w = Math.min(520, Math.max(220, parseInt(localStorage.getItem('pcm_sidebar_w'), 10) || 248)); } catch (_) {}
    document.documentElement.style.setProperty('--sidebar-w', w + 'px');
    const separator = document.querySelector('.mt-resizer');
    if (separator) separator.setAttribute('aria-valuenow', String(w));
  }, []);
  // Pointer capture, not window-level mousemove/mouseup: releasing outside
  // the browser window (alt-tab, a second monitor, over browser chrome)
  // still delivers pointerup/pointercancel to the captured element, so the
  // drag always ends -- a plain window listener would never see a mouseup
  // that happened outside the window and would keep resizing on the next
  // mousemove anywhere on the page.
  const onResizeStart = (e) => {
    const startX = e.clientX, rtl = lang === 'he';
    const start = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w'), 10) || 248;
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);
    document.body.style.userSelect = 'none';
    // The CSS variable is what makes the drag look live; the storage write is
    // only the memory of where it ended. Writing it per pointermove issued
    // hundreds of synchronous storage writes across one drag, so the final
    // width is carried in `last` and written once on release. pointercancel
    // routes here too, which keeps the width the user last saw.
    let last = start;
    const move = (ev) => {
      last = Math.min(520, Math.max(220, start + (rtl ? startX - ev.clientX : ev.clientX - startX)));
      document.documentElement.style.setProperty('--sidebar-w', last + 'px');
      el.setAttribute('aria-valuenow', String(last));
    };
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      document.body.style.userSelect = '';
      try { localStorage.setItem('pcm_sidebar_w', String(last)); } catch (_) {}
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };
  // Keyboard equivalent of the drag above, for the separator's a11y contract.
  // ArrowRight widens in LTR, ArrowLeft widens in RTL (mirrors the drag's own
  // `rtl ? startX - ev.clientX : ev.clientX - startX` sign flip); the other
  // arrow key narrows. Reads/writes the same CSS variable and localStorage
  // key as the drag so the two stay in sync.
  const onResizeStep = (widen) => {
    const cur = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sidebar-w'), 10) || 248;
    const w = Math.min(520, Math.max(220, cur + (widen ? 16 : -16)));
    document.documentElement.style.setProperty('--sidebar-w', w + 'px');
    try { localStorage.setItem('pcm_sidebar_w', String(w)); } catch (_) {}
    document.querySelector('.mt-resizer')?.setAttribute('aria-valuenow', String(w));
  };
  const onResizeKey = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    onResizeStep(lang === 'he' ? e.key === 'ArrowLeft' : e.key === 'ArrowRight');
    e.preventDefault();
  };

  // Sidebar module list. `unassigned_count` isn't folded in here -- the
  // "All forms" badge gets its number from the forms count below, which is
  // authoritative (one source, no race between the two requests).
  useEffect(() => {
    // Fallback only: the tree 404ed, or menu-tree.js never loaded (stale
    // cache, a 404 on the script) -- either way the flat list must render.
    if (!treeUnavailable && window.MenuTree) return;
    PCMAdapter.getModules().then(d => setModules(d.modules)).catch(() => setModules([]));
  }, [treeUnavailable]);

  // Not sticky: a 404 during the first-ever T_MENU walk clears itself once
  // the walk reconciles. Poll once a minute while unavailable; on success the
  // tree remounts and fetches (one extra request per recovery, accepted).
  // A new pick remounts the tree at once instead of waiting for the poll.
  const menuEnv = companyPick ? pageEnv(companyPick.pick) : undefined;
  // Offline, the tree waits for a pick: with no env the server answers its last-synced company (spec §4.3).
  const menuNote = pickPending(companyPick) ? 'loading' : companyPick && !companyPick.pick ? 'unavailable' : null;
  useEffect(() => { setTreeUnavailable(false); }, [menuEnv]);
  useEffect(() => {
    if (!treeUnavailable || menuNote) return;
    const id = setInterval(() => {
      PCMAdapter.getMenuTree(menuEnv).then(() => setTreeUnavailable(false)).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, [treeUnavailable, menuEnv, menuNote]);

  useEffect(() => {
    PCMAdapter.getForms({ limit: 1 })
      .then(d => setFormCount(d.total ?? null))
      .catch(() => {});
  }, []);

  const discardChat = () => {
    chatActivity?.stop();
    setChatVisited(false);
    setChatActivity(null);
  };
  const onLogout = async () => { discardChat(); await PCMAdapter.logoutAndRedirect(); };
  const onSwitchTenant = () => { discardChat(); window.location.href = '/static/index.html?switch=1' + window.location.hash; };
  const onNav = (m, requestedHash) => {
    const hash = requestedHash || (m === '__settings' && activeModule === m ? window.location.hash : {__backups:'#backups', __mcp_proposals:'#mcp-proposals', __jarvis:'#jarvis', __chat:'#chat', __workbench:'#workbench', __studio:'#studio', __settings:'#agent', __sync:'#sync', __jobs:'#jobs', __outlook:'#outlook', __freshness:'#freshness', __team:'#team', __team_offline:'#team-offline', __llm_usage:'#llm-usage'}[m] || '');
    window.history.pushState(null, '', window.location.pathname + hash);
    setTarget({module:m}); setActiveModule(m); setOpenForm(null);
  };
  const onPickForm = (ename) => { onNav(null); setOpenForm(ename); };
  const onAsk = (ename, type) => {
    const kind = type === 'proc' ? (lang === 'he' ? 'התהליך' : 'procedure') : (lang === 'he' ? 'הדוח' : 'report');
    setChatDraft(lang === 'he' ? `מה עושה ${kind} ${ename}?` : `What does the ${kind} ${ename} do?`);
    onNav('__chat');
  };

  const viewBody = activeModule === '__backups'
    ? <window.BackupsPage lang={lang}/>
    : activeModule === '__mcp_proposals'
    ? <window.McpProposalsPage key={target.proposalId || 'list'} lang={lang} proposalId={target.proposalId}/>
    : activeModule === '__llm_usage'
    ? <window.LlmUsagePage lang={lang} role={role}/>
    : activeModule === '__jarvis'
    ? <window.JarvisPage lang={lang} companyPick={companyPick} onOpenSettings={() => onNav('__settings', '#voice')}/>
    : activeModule === '__team_offline'
    ? (me?.team_admin_offline && !me.via_admin && <window.TeamOfflinePage lang={lang}/>)
    : activeModule === '__team'
    ? (window.TeamPage
        ? <window.TeamPage lang={lang}/>
        : <div className="card"><div className="card-body">{lang === 'he' ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : 'This page failed to load — a hard reload may be required.'}</div></div>)
    : activeModule === '__freshness'
    ? <window.MirrorFreshnessPage lang={lang} companyPick={companyPick}/>
    : activeModule === '__sync'
      ? <window.SyncControlPage lang={lang}/>
      : activeModule === '__jobs'
        ? (window.OutgoingJobsPage
            ? <window.OutgoingJobsPage lang={lang}/>
            : <div className="card"><div className="card-body">{lang === 'he' ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : 'Outgoing jobs failed to load — a hard reload may be required.'}</div></div>)
        : activeModule === '__outlook'
          ? (window.OutlookPage
              ? <window.OutlookPage lang={lang}/>
              : <div className="card"><div className="card-body">{lang === 'he' ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : 'Outlook failed to load — a hard reload may be required.'}</div></div>)
        : activeModule === '__workbench'
          ? (window.WorkbenchPage
              ? <window.WorkbenchPage key={`${target.runId || 0}:${target.approvalId || 0}`} lang={lang} target={target} companyPick={companyPick}/>
              : <div className="card"><div className="card-body">{lang === 'he' ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : 'Agent workbench failed to load — a hard reload may be required.'}</div></div>)
        : activeModule === '__studio'
          ? (window.StudioPage
              ? <window.StudioPage lang={lang} target={target} companyPick={companyPick} onOpenSettings={() => onNav('__settings')}/>
              : <div className="card"><div className="card-body">{lang === 'he' ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : 'Studio failed to load — a hard reload may be required.'}</div></div>)
        : activeModule === '__settings'
          ? <window.SettingsPage lang={lang} me={me}/>
          : activeModule === '__chat'
            ? null
            : openForm
              ? <FormDetailView ename={openForm} lang={lang} onBack={() => setOpenForm(null)}
                                module={activeModule}/>
              : <CatalogView lang={lang} module={activeModule} onPick={setOpenForm} modules={modules}/>;

  // Truthful breadcrumb label: the open form's ENAME, else the active
  // module's display name, else "All forms" -- never a stale 'overview'.
  const viewLabel = activeModule === '__backups'
    ? (lang === 'he' ? 'גיבויים' : 'Backups')
    : activeModule === '__mcp_proposals'
    ? (lang === 'he' ? 'הצעות לשינויים בניהול' : 'Administrative proposals')
    : activeModule === '__llm_usage'
    ? (lang==='he'?'שימוש במודלי שפה':'LLM Usage')
    : activeModule === '__jarvis'
    ? 'Jarvis'
    : activeModule === '__team_offline'
    ? 'Team Admin (offline)'
    : activeModule === '__team'
    ? (lang === 'he' ? 'הסוכן והצוות שלי' : 'My agent and team')
    : activeModule === '__freshness'
    ? (lang === 'he' ? 'טריות המראה' : 'Mirror freshness')
    : activeModule === '__sync'
      ? (lang === 'he' ? 'בקרת סנכרון' : 'Sync control')
      : activeModule === '__jobs'
        ? (lang === 'he' ? 'עבודות יוצאות' : 'Outgoing jobs')
        : activeModule === '__outlook'
          ? 'Outlook'
        : activeModule === '__workbench'
          ? (lang === 'he' ? 'סביבת עבודה לסוכן' : 'Agent workbench')
        : activeModule === '__studio'
          ? (lang === 'he' ? 'סטודיו' : 'Studio')
        : activeModule === '__settings'
          ? (lang === 'he' ? 'הגדרות' : 'Settings')
          : activeModule === '__chat'
            ? (lang === 'he' ? 'שיחה עם הנתונים' : 'Talk to your data')
            : openForm
              ? openForm
              : activeModule == null
                ? (lang === 'he' ? 'כל המסכים' : 'All forms')
                : (modules.find(m => m.module === activeModule)?.module_name || activeModule);

  useEffect(() => { document.title = `${viewLabel} · Priority Monitor`; }, [viewLabel]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main-content" onClick={event => { event.preventDefault(); mainRef.current?.focus(); }}>{lang === 'he' ? 'דלג לתוכן' : 'Skip to content'}</a>
      <Sidebar active={activeModule} onNav={onNav} lang={lang}
               tenantLabel={tenantLabel} role={role} me={me}
               modules={modules} formCount={formCount}
               onSwitchTenant={onSwitchTenant}
               openForm={openForm} onPickForm={onPickForm} onAsk={onAsk}
               treeUnavailable={treeUnavailable} onTreeUnavailable={() => setTreeUnavailable(true)} menuEnv={menuEnv} menuNote={menuNote}
               onResizeStart={onResizeStart} onResizeKey={onResizeKey} onResizeStep={onResizeStep}/>
      <div className="main">
        <Topbar now={now} lang={lang} setLang={setLang} viewLabel={viewLabel}
                onLogout={onLogout} onSwitchTenant={onSwitchTenant} onSwitchContext={() => setContextOpen(true)} role={role}
                lastIngestAt={null} lastIngestSource={null}
                nextIngestAt={null} nextIngestSource={null}
                chatActivity={chatActivity} onReturnToChat={() => onNav('__chat')} companyPick={companyPick}
                me={me} onChangePassword={() => setPwOpen(true)} onMcpTokens={() => setMcpOpen(true)} onMcpProposals={() => onNav('__mcp_proposals')}/>
        <main id="main-content" ref={mainRef} tabIndex={-1} aria-label={viewLabel} className="content">
          {backupCloseUnconfirmed && <p className="set-error" role="alert">{lang === 'he'
            ? 'לא ניתן לאשר שגישת הגיבויים נסגרה. תוקפה יפוג בתוך 15 דקות.'
            : 'Backup access could not be closed. It expires within 15 minutes.'}</p>}
          {chatVisited && (
            <div className="chat-page" hidden={activeModule !== '__chat'}>
              {window.ChatPage
                ? <window.ChatPage lang={lang} role={role} companyPick={companyPick} onOpenSettings={() => onNav('__settings')}
                                   onOpenWorkbench={() => onNav('__workbench')}
                                   visible={activeModule === '__chat'} onActivityChange={setChatActivity}
                                   initialDraft={chatDraft} onDraftConsumed={() => setChatDraft('')}
                                   initialTaskId={linkedTaskId} onTaskIdConsumed={() => setLinkedTaskId(null)}/>
                : <div className="card"><div className="card-body">{lang === 'he' ? 'הלשונית לא נטענה — נסו רענון מלא של הדף.' : 'Chat failed to load — a hard reload may be required.'}</div></div>}
            </div>
          )}
          {viewBody}
        </main>
      </div>
      {pwOpen && window.ChangePasswordDialog && <window.ChangePasswordDialog lang={lang} onClose={() => setPwOpen(false)}/>}
      {mcpOpen && window.McpTokensDialog && <window.McpTokensDialog lang={lang} onClose={() => setMcpOpen(false)}/>}
      {contextOpen && <ContextDialog lang={lang} onClose={() => setContextOpen(false)}/>}
    </div>
  );
}

// Consent has no viewed company: an installation admin signs in with only their
// own admin token, so do not mount catalog/sidebar requests needing a company.
function McpOAuthEntry({ requestId }) {
  const [lang, setLang] = useState(readLang);
  const [me, setMe] = useState(null);
  useEffect(() => { PCMAdapter.getMe().then(setMe).catch(() => {}); }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    try { localStorage.setItem(LANG_KEY, lang); } catch (_) { /* private mode: in-memory only */ }
  }, [lang]);
  return <div className="main">
    <Topbar now={new Date()} lang={lang} setLang={setLang} role={PCMAdapter.getRole()}
      viewLabel={lang === 'he' ? 'חיבור יישום' : 'Connect app'} me={me}
      onLogout={() => PCMAdapter.logoutAndRedirect()}/>
    <main id="main-content" tabIndex={-1} className="content">
      <window.McpOAuthConsent requestId={requestId} lang={lang}/>
    </main>
  </div>;
}
const oauthRequest = PCMAdapter.mcpOAuthRequest();
ReactDOM.createRoot(document.getElementById('app')).render(oauthRequest ? <McpOAuthEntry requestId={oauthRequest}/> : <App/>);
